import { Injectable } from '@angular/core';
import { SessionService } from './session.service';
import { User } from '@models/user';

/**
 * GTM Data Layer Service
 * 
 * This service provides methods to push events to Google Tag Manager's dataLayer.
 * All methods ensure the dataLayer exists before pushing events.
 * 
 * Usage Examples:
 * 
 * // Sign Up Event
 * this.gtmService.pushSignUp('email');
 * 
 * // Add to Cart Event
 * this.gtmService.pushAddToCart({
 *   itemId: 'Knockio-pro',
 *   itemName: 'Professional',
 *   price: 20,
 *   quantity: 13,
 *   currency: 'USD'
 * });
 * 
 * // Begin Checkout Event
 * this.gtmService.pushBeginCheckout({
 *   itemId: 'Knockio-pro',
 *   itemName: 'Professional',
 *   price: 20,
 *   quantity: 13,
 *   currency: 'USD'
 * });
 * 
 * // Purchase Event
 * this.gtmService.pushPurchase({
 *   transactionId: 'T_12345_3',
 *   itemId: 'Knockio-pro',
 *   itemName: 'Professional',
 *   price: 20,
 *   quantity: 13,
 *   currency: 'USD'
 * });
 * 
 * // Working Lead Event
 * this.gtmService.pushWorkingLead({
 *   value: 30.03,
 *   currency: 'USD',
 *   leadStatus: 'Started conversations'
 * });
 * 
 * // Generic Event (for custom events)
 * this.gtmService.pushEvent('custom_event', { customData: 'value' });
 */

declare global {
  interface Window {
    dataLayer: any[];
  }
}

export interface EcommerceItem {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
}

export interface EcommerceData {
  currency: string;
  value: number;
  items: EcommerceItem[];
  transaction_id?: string;
}

export interface AddToCartParams {
  itemId: string;
  itemName: string;
  price: number;
  quantity: number;
  currency?: string;
}

export interface BeginCheckoutParams {
  itemId: string;
  itemName: string;
  price: number;
  quantity: number;
  currency?: string;
}

export interface PurchaseParams {
  transactionId: string;
  itemId: string;
  itemName: string;
  price: number;
  quantity: number;
  currency?: string;
}

export interface WorkingLeadParams {
  value: number;
  currency?: string;
  leadStatus: string;
}

@Injectable({
  providedIn: 'root'
})
export class GtmService {
  private readonly DEFAULT_CURRENCY = 'USD';

  constructor(private sessionService: SessionService) {}

  /**
   * Initialize dataLayer if it doesn't exist
   */
  private ensureDataLayer(): void {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
    }
  }

  /**
   * Get user data formatted for GTM user_data
   * @returns User data object for GTM
   */
  private getUserData(): any {
    const user: User | null = this.sessionService.getUserValue();
    
    if (!user) {
      return {
        isLoggedIn: false,
        id: "",
        email: "",
        phone_number: "",
        first_name: "",
        last_name: "",
        street: "",
        city: "",
        region: "",
        country: "",
        postal_code: ""
      };
    }

    // Use firstName/lastName if available, otherwise parse from name field
    let firstName = user.firstName || "";
    let lastName = user.lastName || "";
    
    if (!firstName && !lastName && user.name) {
      const nameParts = user.name.trim().split(/\s+/);
      firstName = nameParts.length > 0 ? nameParts[0] : "";
      lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";
    }

    return {
      isLoggedIn: true,
      id: user.id ? String(user.id) : "",
      email: user.email || "",
      phone_number: "", // Not available in current user model
      first_name: firstName,
      last_name: lastName,
      street: "", // Not available in current user model
      city: "", // Not available in current user model
      region: "", // Not available in current user model
      country: "", // Not available in current user model
      postal_code: "" // Not available in current user model
    };
  }

  /**
   * Push user_data event to dataLayer as a separate event
   */
  private pushUserData(): void {
    const userData = this.getUserData();
    this.pushEvent('user_data', {
      user_data: userData
    });
  }

  /**
   * Generic method to push any event to dataLayer
   * @param eventName - Name of the event
   * @param data - Data object to push
   */
  pushEvent(eventName: string, data: any = {}): void {
    this.ensureDataLayer();
    
    if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
        event: eventName,
        ...data
      });
    }
  }

  /**
   * Push sign_up event to dataLayer
   * @param method - Sign up method ('email', 'google', 'facebook', etc.)
   */
  pushSignUp(method: string = 'email'): void {
    this.pushEvent('sign_up', {
      method: method
    });
  }

  /**
   * Push add_to_cart event to dataLayer
   * @param params - Cart item parameters
   */
  pushAddToCart(params: AddToCartParams): void {
    const currency = params.currency || this.DEFAULT_CURRENCY;
    const totalValue = params.price * params.quantity;

    this.pushEvent('add_to_cart', {
      ecommerce: {
        currency: currency,
        value: totalValue,
        items: [
          {
            item_id: params.itemId,
            item_name: params.itemName,
            price: params.price,
            quantity: params.quantity
          }
        ]
      }
    });
    
    // Push separate user_data event after add_to_cart
    this.pushUserData();
  }

  /**
   * Push begin_checkout event to dataLayer
   * @param params - Checkout item parameters
   */
  pushBeginCheckout(params: BeginCheckoutParams): void {
    const currency = params.currency || this.DEFAULT_CURRENCY;
    const totalValue = params.price * params.quantity;

    this.pushEvent('begin_checkout', {
      ecommerce: {
        currency: currency,
        value: totalValue,
        items: [
          {
            item_id: params.itemId,
            item_name: params.itemName,
            price: params.price,
            quantity: params.quantity
          }
        ]
      }
    });
    
    // Push separate user_data event after begin_checkout
    this.pushUserData();
  }

  /**
   * Push purchase event to dataLayer
   * @param params - Purchase parameters including transaction ID
   */
  pushPurchase(params: PurchaseParams): void {
    const currency = params.currency || this.DEFAULT_CURRENCY;
    const totalValue = params.price * params.quantity;

    this.pushEvent('purchase', {
      ecommerce: {
        transaction_id: params.transactionId,
        currency: currency,
        value: totalValue,
        items: [
          {
            item_id: params.itemId,
            item_name: params.itemName,
            price: params.price,
            quantity: params.quantity
          }
        ]
      }
    });
    
    // Push separate user_data event after purchase
    this.pushUserData();
  }

  /**
   * Push working_lead event to dataLayer
   * @param params - Working lead parameters
   */
  pushWorkingLead(params: WorkingLeadParams): void {
    const currency = params.currency || this.DEFAULT_CURRENCY;

    this.pushEvent('working_lead', {
      currency: currency,
      value: params.value,
      lead_status: params.leadStatus
    });
  }
}

